import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Get initial values from localStorage
  const getInitialUser = () => {
    try {
      const saved = localStorage.getItem('user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const [user, setUser] = useState(getInitialUser);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);

  // Add partial auth state for 2FA flow
  const [partialAuth, setPartialAuth] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);

  const navigate = useNavigate();

  // Complete login helper - defined first since it's used by other functions
  const completeLogin = useCallback(async (token, userData) => {
    try {
      // Save to storage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Update state
      setUser(userData);
      setIsAuthenticated(true);
      setPartialAuth(null);
      setRequires2FA(false);

      // Navigate based on role
      const roleRoutes = {
        customer: '/customer/dashboard',
        bodyguard: '/bodyguard/dashboard',
        admin: '/admin/dashboard'
      };

      const route = roleRoutes[userData.role] || '/';
      navigate(route);

      toast.success(`Welcome back, ${userData.fullName}!`);

      return { success: true };
    } catch (error) {
      logger.error('Complete login error:', error);
      return { success: false, error: error.message };
    }
  }, [navigate]);

  // Login to handle 2FA flow
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);

      // Check for trusted device ID
      const deviceId = localStorage.getItem('guardly_device_id');

      const loginData = {
        ...credentials,
        ...(deviceId && { deviceId })
      };

      const response = await authAPI.login(loginData);
      const {
        token,
        user: userData,
        requiresOTP,
        partialToken,
        message,
        trustedDevice
      } = response.data;

      // Check if login is complete (no 2FA required or trusted device)
      if (token && !requiresOTP) {
        // Direct login - 2FA is disabled or trusted device
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));

        setUser(userData);
        setIsAuthenticated(true);

        const route = `/${userData.role}/dashboard`;
        navigate(route);

        if (trustedDevice) {
          toast.success('Welcome back! (Trusted device)');
        } else {
          toast.success('Welcome back!');
        }

        return { success: true };
      }

      // 2FA is required
      if (requiresOTP) {
        setPartialAuth({
          partialToken,
          email: credentials.email,
          userData
        });
        setRequires2FA(true);
        toast.success(message || 'Verification code sent to your email');
        return { success: true, requiresOTP: true };
      }

      // Shouldn't reach here
      return { success: false, error: 'Unexpected response from server' };

    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
      return { success: false, error: error.response?.data?.message };
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const verifyOTP = useCallback(async (otp, trustDevice = false) => {
    logger.debug('AuthContext verifyOTP called:', {
      hasOTP: !!otp,
      hasPartialAuth: !!partialAuth,
      hasPartialToken: !!partialAuth?.partialToken
    });

    if (!partialAuth || !partialAuth.partialToken) {
      toast.error('Session expired. Please login again.');
      setPartialAuth(null);
      setRequires2FA(false);
      return { success: false, error: 'No partial token available' };
    }

    try {
      setLoading(true);

      // Make sure we're passing the partial token
      const response = await authAPI.verifyOTP(
        otp,
        partialAuth.partialToken,
        trustDevice
      );

      const { token, user: userData, deviceId } = response.data;

      logger.debug('OTP verification successful:', { hasToken: !!token, hasUser: !!userData });

      // Store device ID if trusted
      if (deviceId) {
        localStorage.setItem('guardly_device_id', deviceId);
      }

      // Complete the login
      return await completeLogin(token, userData);
    } catch (error) {
      logger.error('OTP verification error:', error);
      const message = error.response?.data?.message || 'Invalid verification code';
      toast.error(message);
      return {
        success: false,
        error: message,
        remainingAttempts: error.response?.data?.remainingAttempts
      };
    } finally {
      setLoading(false);
    }
  }, [partialAuth, completeLogin]);

  const resendOTP = useCallback(async () => {
    logger.debug('AuthContext resendOTP called:', {
      hasPartialAuth: !!partialAuth,
      hasPartialToken: !!partialAuth?.partialToken
    });

    if (!partialAuth || !partialAuth.partialToken) {
      toast.error('Session expired. Please login again.');
      setPartialAuth(null);
      setRequires2FA(false);
      return { success: false, error: 'No partial token available' };
    }

    try {
      setLoading(true);

      // Make sure we're passing the partial token
      const response = await authAPI.resendOTP(partialAuth.partialToken);
      const { message, attemptsRemaining } = response.data;

      toast.success(message || 'New verification code sent');
      return {
        success: true,
        attemptsRemaining
      };
    } catch (error) {
      logger.error('Resend OTP error:', error);
      const errorData = error.response?.data;

      if (error.response?.status === 429) {
        toast.error(errorData.message || 'Too many requests. Please try again later.');
        return {
          success: false,
          retryAfter: errorData.retryAfter
        };
      }

      toast.error(errorData?.message || 'Failed to resend code');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [partialAuth]);

  // Cancel 2FA process and go back to login
  const cancel2FA = useCallback(() => {
    setPartialAuth(null);
    setRequires2FA(false);
  }, []);

  // Toggle 2FA settings
  const toggle2FA = useCallback(async (enable, password) => {
    try {
      setLoading(true);
      const response = await authAPI.toggle2FA(enable, password);
      const { message, backupCodes, user: updatedUserData } = response.data;

      // Update the user state with the new 2FA status
      if (updatedUserData) {
        // Update local storage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const newUser = {
          ...currentUser,
          ...updatedUserData,
          twoFactorAuth: updatedUserData.twoFactorAuth
        };

        localStorage.setItem('user', JSON.stringify(newUser));

        // Update state
        setUser(newUser);
      }

      toast.success(message);

      return {
        success: true,
        backupCodes: backupCodes || []
      };
    } catch (error) {
      logger.error('Toggle 2FA error:', error);
      toast.error(error.response?.data?.message || 'Failed to update 2FA settings');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if device is trusted
  const checkTrustedDevice = useCallback(() => {
    const deviceId = localStorage.getItem('guardly_device_id');
    return !!deviceId;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Don't remove device ID on logout - it should persist
    setUser(null);
    setIsAuthenticated(false);
    setPartialAuth(null);
    setRequires2FA(false);
    navigate('/login');
  }, [navigate]);

  const updateProfile = useCallback(async (data) => {
    try {
      setLoading(true);
      const response = await authAPI.updateProfile(data);
      const updatedUser = response.data.user;

      // Update local storage
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Update state
      setUser(updatedUser);

      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
      return {
        success: false,
        error: error.response?.data?.message || 'Update failed'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(async (passwordData) => {
    try {
      setLoading(true);
      await authAPI.changePassword(passwordData);
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
      return {
        success: false,
        error: error.response?.data?.message || 'Password change failed'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      const { token, user: newUser } = response.data;

      // Save to storage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(newUser));

      // Update state
      setUser(newUser);
      setIsAuthenticated(true);

      if (newUser.role === 'bodyguard') {
        navigate('/bodyguard/dashboard');
        toast.success('Registration successful! Please complete your application to start accepting bookings.');
      } else {
        navigate('/customer/dashboard');
        toast.success('Registration successful!');
      }

      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    register,
    requires2FA,
    partialAuth,
    verifyOTP,
    resendOTP,
    cancel2FA,
    toggle2FA,
    checkTrustedDevice
  }), [
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    register,
    requires2FA,
    partialAuth,
    verifyOTP,
    resendOTP,
    cancel2FA,
    toggle2FA,
    checkTrustedDevice
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
